import type { SyntheticEvent } from "react";
import { twMerge } from "tailwind-merge";

import type { InternalOnlyRender } from "@/hooks/use-internal-only-render";
import type { Nullable } from "@/types/utils";
import { numberIsTruthyOrZero } from "@/utils/helpers";

export const MANTINE_COLORS = {
  blue: "var(--uw-blue)",
  green: "var(--uw-green)",
  orange: "var(--uw-orange)",
  primary: "var(--uw-purple)",
  red: "var(--uw-red)",
  yellow: "var(--uw-yellow)",
};

export const rowColorCodes = {
  red: "#ffd6d4",
  orange: "#ffedd6",
  yellow: "#ffffdc",
  green: "#d6e8d2",
  blue: "#ceceff",
  purple: "#e9d1e7",
};
export const borderColorCodes = {
  red: "#ff0000",
  orange: "#ffa500",
  yellow: "#ffff00",
  green: "#008000",
  blue: "#0000ff",
  purple: "#800080",
};

export const getJobNameWithDisplayName = (
  jobName?: string,
  internalName?: Nullable<string>,
) =>
  jobName || internalName
    ? `${jobName}${internalName ? ` | ${internalName}` : ""}`
    : "";

export const stopPropagationWrapper =
  <T extends Element>(callback?: (event: SyntheticEvent<T>) => void) =>
  (e: SyntheticEvent<T>) => {
    e.stopPropagation();

    callback?.(e);
  };

// The actual color values have changed over time. Rather than migrate them all
// in the DB anytime there is a change we can just keep a map of generic names
// to current color codes.
const HIGHLIGHT_COLOR_MAP = {
  blue: MANTINE_COLORS.blue,
  green: MANTINE_COLORS.green,
  orange: "#d97601",
  purple: MANTINE_COLORS.primary,
  red: "var(--mantine-color-red-8)",
  yellow: MANTINE_COLORS.yellow,
} as const;

export type Colors = Nullable<keyof typeof HIGHLIGHT_COLOR_MAP>;

export const getCodeForHighlightColor = (color?: Colors) =>
  color ? HIGHLIGHT_COLOR_MAP[color] : "transparent";

export const HIGHLIGHT_OPTIONS: {
  value: Colors;
  label: string;
}[] = [
  { value: "red", label: "Red" },
  { value: "orange", label: "Orange" },
  { value: "yellow", label: "Yellow" },
  { value: "green", label: "Green" },
  { value: "blue", label: "Blue" },
  { value: "purple", label: "Purple" },
  { value: null, label: "None" },
] as const;

export const getNameForHighlightColor = (colorCode: string) => {
  const mapName = Object.keys(HIGHLIGHT_COLOR_MAP).find(
    (colorName) =>
      HIGHLIGHT_COLOR_MAP[colorName as keyof typeof HIGHLIGHT_COLOR_MAP] ===
      colorCode,
  );
  const accessibleName = HIGHLIGHT_OPTIONS.find(
    ({ value }) => value === mapName,
  );

  return accessibleName?.label ?? "Unknown color name";
};

export const LAYOUT_DIMENSIONS = {
  FILTER_MENU_WIDTH_PX: 224,
  HEADER_HEIGHT_PX: 72,
  IMPERSONATION_BANNER_HEIGHT_PX: 48,
  TABLE_HEADER_HEIGHT_PX: 48,
  get combinedHeaderHeight() {
    return this.HEADER_HEIGHT_PX + this.TABLE_HEADER_HEIGHT_PX;
  },
  get tableViewContentHeight() {
    return `calc(100dvh - ${this.combinedHeaderHeight}px)`;
  },
  get internalUserTableViewContentHeight() {
    return `calc(100dvh - ${this.combinedHeaderHeight + this.IMPERSONATION_BANNER_HEIGHT_PX}px)`;
  },
};

export const getTableViewContentHeight = (isInternal = false) =>
  isInternal
    ? LAYOUT_DIMENSIONS.internalUserTableViewContentHeight
    : LAYOUT_DIMENSIONS.tableViewContentHeight;

export const withRowLoadingStyles = (loadingIds: string[], rowId: string) =>
  loadingIds.includes(rowId)
    ? twMerge(["opacity-50", "pointer-events-none"])
    : "";

export const capitalizeFirstLetter = (str: string, lowerCaseRest = false) =>
  str.charAt(0).toUpperCase() +
  (lowerCaseRest ? str.slice(1).toLowerCase() : str.slice(1));

// Util to reduce duplicated logic around rendering raw scores for internal
// users. For use with useInternalOnlyRender hook
export const renderRawScore = (
  renderFn: InternalOnlyRender,
  score?: Nullable<number> | string,
) =>
  renderFn(` (${score})`, {
    additionalConditional:
      typeof score === "string" || numberIsTruthyOrZero(score),
  });
